/**
 * RoomTapHandler - Explicit handler for Room Arrival Tags
 * 
 * URL: /standalone/room/{TAG_ID}
 * 
 * This component ONLY handles room arrival/check-in tags.
 * It calls /api/hotels-standalone/room/{tag_id} which ONLY looks in the hotel_rooms collection.
 * 
 * If the room tag doesn't exist, it shows an error - it does NOT fall back to review cards.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, ClipboardList, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function RoomTapHandler() {
  const { tagId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tapData, setTapData] = useState(null);

  useEffect(() => {
    const handleTap = async () => {
      try {
        const url = `${API_URL}/api/hotels-standalone/room/${tagId}`;
        
        // Use fetch with redirect: 'manual' to detect 302 vs error
        const response = await fetch(url, {
          method: 'GET',
          redirect: 'manual'
        });
        
        // Status 0 or 'opaqueredirect' type means we got a cross-origin redirect (302)
        // This is SUCCESS - redirect the browser to the API and let it follow
        if (response.status === 0 || response.type === 'opaqueredirect') {
          window.location.href = url;
          return;
        }
        
        // If we got JSON response, it's an error
        if (response.headers.get('content-type')?.includes('application/json')) {
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.detail || 'Room tag not found');
          }
          // If somehow we got a success JSON with redirect_url
          if (data.redirect_url) {
            const fullUrl = data.redirect_url.startsWith('http') 
              ? data.redirect_url 
              : `${window.location.origin}${data.redirect_url}`;
            window.location.href = fullUrl;
            return;
          }
        }
        
        // Fallback: redirect to API anyway (let browser handle)
        window.location.href = url;
      } catch (err) {
        console.error('Room tap error:', err);
        setError(err.message || 'Failed to load room tag');
        setLoading(false);
      }
    };

    if (tagId) {
      handleTap();
    }
  }, [tagId]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Room Tag Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Tag ID: {tagId}
            </p>
            <p className="text-xs text-gray-400 mt-4">
              If this is a review engine card, use /standalone/review/{tagId} instead.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          <Loader2 className="w-12 h-12 text-green-600 mx-auto mb-4 animate-spin" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Welcome!
          </h1>
          <p className="text-gray-600">
            Loading your check-in survey...
          </p>
          {tapData && (
            <div className="mt-4 text-sm text-gray-500">
              <p>Room: {tapData.room_identifier}</p>
              <ClipboardList className="w-4 h-4 inline ml-2" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
